import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Tag, statusTone, Loader, Empty } from "@/components/kit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  Users, ShieldCheck, Activity, AlertTriangle, TrendingUp, MapPin, Landmark, Briefcase, IndianRupee, GraduationCap,
} from "lucide-react";

const COLORS = ["#F43F5E", "#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#06B6D4", "#EC4899", "#84CC16"];

export default function Intelligence() {
  const [wf, setWf] = useState(null);
  const [sd, setSd] = useState(null);
  const [gov, setGov] = useState(null);

  useEffect(() => {
    api.get("/intelligence/workforce").then((r) => setWf(r.data)).catch(() => setWf(false));
    api.get("/intelligence/supply-demand").then((r) => setSd(r.data)).catch(() => {});
    api.get("/intelligence/government").then((r) => setGov(r.data)).catch(() => {});
  }, []);

  return (
    <div className="fade-up">
      <PageHeader overline="Workforce intelligence" title="Krida Intelligence"
        subtitle="Analytics, supply-demand and permission-controlled government dashboards from the sports workforce graph." testid="krida-intelligence-page" />

      <Tabs defaultValue="workforce">
        <TabsList className="bg-krida-surface border border-white/10 mb-6">
          <TabsTrigger value="workforce" data-testid="tab-intel-workforce" className="data-[state=active]:bg-krida-crimson data-[state=active]:text-white gap-2"><Activity className="w-4 h-4" /> Workforce</TabsTrigger>
          <TabsTrigger value="supply" data-testid="tab-intel-supply" className="data-[state=active]:bg-krida-crimson data-[state=active]:text-white gap-2"><TrendingUp className="w-4 h-4" /> Supply-Demand</TabsTrigger>
          <TabsTrigger value="gov" data-testid="tab-intel-gov" className="data-[state=active]:bg-krida-crimson data-[state=active]:text-white gap-2"><Landmark className="w-4 h-4" /> Government</TabsTrigger>
        </TabsList>

        <TabsContent value="workforce">
          {wf === null ? <Loader /> : !wf ? <Empty /> : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                <StatCard label="Total workforce" value={wf.totals.total} icon={Users} accent="cobalt" />
                <StatCard label="Verified" value={wf.totals.verified} icon={ShieldCheck} accent="emerald" />
                <StatCard label="Available" value={wf.totals.available} icon={Activity} accent="amber" />
                <StatCard label="Deployed" value={wf.totals.deployed} icon={Briefcase} accent="crimson" />
                <StatCard label="Expiring creds" value={wf.totals.credential_expiring} icon={AlertTriangle} accent="crimson" />
              </div>
              <div className="grid lg:grid-cols-2 gap-6">
                <ChartCard title="Workforce by sport"><BarWrap data={wf.by_sport} /></ChartCard>
                <ChartCard title="Workforce by role"><BarWrap data={wf.by_role} color="#3B82F6" /></ChartCard>
                <ChartCard title="Workforce by city"><BarWrap data={wf.by_city} color="#10B981" /></ChartCard>
                <ChartCard title="Distribution by level"><PieWrap data={wf.by_level} /></ChartCard>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="supply">
          {sd === null ? <Loader /> : !sd ? <Empty /> : (
            <>
              <div className="krida-card p-4 mb-4">
                <div className="overline text-krida-crimson mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Top role shortages</div>
                <div className="flex flex-wrap gap-2">{sd.shortages.map((r) => <Tag key={r.role} tone="crimson">{r.role}: gap {r.gap}</Tag>)}</div>
              </div>
              <div className="krida-card p-6">
                <h3 className="font-heading text-xl font-bold mb-4">Supply vs demand by role</h3>
                <div style={{ width: "100%", height: 300 }} data-testid="sd-chart">
                  <ResponsiveContainer>
                    <BarChart data={sd.rows.slice(0, 8)} margin={{ left: 0, right: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                      <XAxis dataKey="role" stroke="#64748B" fontSize={10} angle={-20} textAnchor="end" height={60} interval={0} />
                      <YAxis stroke="#64748B" fontSize={11} />
                      <Tooltip contentStyle={{ background: "#131C2E", border: "1px solid #1E293B", borderRadius: 12, color: "#fff" }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                      <Bar dataKey="supply" fill="#10B981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="demand" fill="#F43F5E" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="krida-card p-6 mt-6">
                <h3 className="font-heading text-xl font-bold mb-4">Detailed supply-demand table</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left overline text-slate-500 border-b border-white/5"><th className="pb-3 font-mono">Role</th><th className="pb-3 font-mono text-right">Supply</th><th className="pb-3 font-mono text-right">Demand</th><th className="pb-3 font-mono text-right">Gap</th><th className="pb-3 font-mono">Status</th></tr></thead>
                    <tbody>
                      {sd.rows.map((r) => (
                        <tr key={r.role} className="border-b border-white/5" data-testid={`sd-row-${r.role}`}>
                          <td className="py-2.5">{r.role}</td>
                          <td className="py-2.5 text-right font-mono text-krida-emerald">{r.supply}</td>
                          <td className="py-2.5 text-right font-mono text-krida-cobalt">{r.demand}</td>
                          <td className="py-2.5 text-right font-mono">{r.gap}</td>
                          <td className="py-2.5"><Tag tone={statusTone(r.status)}>{r.status}</Tag></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="gov">
          {gov === null ? <Loader /> : !gov ? <Empty /> : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard label="Registered" value={gov.headline.registered} icon={Users} accent="cobalt" />
                <StatCard label="Verified" value={gov.headline.verified} icon={ShieldCheck} accent="emerald" />
                <StatCard label="Assignments generated" value={gov.headline.assignments_generated} icon={Briefcase} accent="amber" />
                <StatCard label="Employment created" value={gov.headline.employment_created} icon={TrendingUp} accent="crimson" />
                <StatCard label="Earnings disbursed" value={`₹${(gov.headline.earnings_disbursed || 0).toLocaleString("en-IN")}`} icon={IndianRupee} accent="emerald" />
                <StatCard label="Training completed" value={gov.headline.training_completed} icon={GraduationCap} accent="cobalt" />
                <StatCard label="Active workforce" value={gov.headline.active} icon={Activity} accent="amber" />
                <StatCard label="Utilisation" value={`${gov.workforce_utilisation}%`} icon={TrendingUp} accent="crimson" />
              </div>
              <div className="grid lg:grid-cols-2 gap-6">
                <ChartCard title="Workforce by district" icon={MapPin}><BarWrap data={gov.by_district} color="#F59E0B" /></ChartCard>
                <ChartCard title="Workforce by sport"><PieWrap data={gov.by_sport} /></ChartCard>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ChartCard({ title, children, icon: Icon }) {
  return (
    <div className="krida-card p-6">
      <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-slate-400" />}{title}</h3>
      <div style={{ width: "100%", height: 280 }}>{children}</div>
    </div>
  );
}

function BarWrap({ data, color = "#F43F5E" }) {
  return (
    <ResponsiveContainer>
      <BarChart data={data} layout="vertical" margin={{ left: 10, right: 10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" horizontal={false} />
        <XAxis type="number" stroke="#64748B" fontSize={11} />
        <YAxis type="category" dataKey="name" stroke="#94A3B8" fontSize={11} width={90} />
        <Tooltip contentStyle={{ background: "#131C2E", border: "1px solid #1E293B", borderRadius: 12, color: "#fff" }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
        <Bar dataKey="value" fill={color} radius={[0, 6, 6, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function PieWrap({ data }) {
  return (
    <ResponsiveContainer>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={55} paddingAngle={2}>
          {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        <Tooltip contentStyle={{ background: "#131C2E", border: "1px solid #1E293B", borderRadius: 12, color: "#fff" }} />
      </PieChart>
    </ResponsiveContainer>
  );
}
